/**
 * Theme selection for portal-hosted deployments (port of GIS-EYE themeController).
 * Precedence: ?theme= query -> gs_theme cookie -> stored ultimate-gis-theme ->
 * prefers-color-scheme (default dark). A tiny inline copy runs in
 * public/index.html to avoid a flash of the wrong theme; keep them in sync.
 */
export const THEME_STORAGE_KEY = 'ultimate-gis-theme';
const VALID = new Set(['light', 'dark']);

export function resolveTheme({ search = '', cookie = '', stored = null, prefersLight = false } = {}) {
  try {
    const q = new URLSearchParams(search).get('theme');
    if (VALID.has(q)) return { theme: q, source: 'query' };
  } catch { /* fall through */ }
  const m = /(?:^|;\s*)gs_theme=(light|dark)(?:;|$)/.exec(cookie || '');
  if (m) return { theme: m[1], source: 'cookie' };
  if (VALID.has(stored)) return { theme: stored, source: 'stored' };
  return { theme: prefersLight ? 'light' : 'dark', source: 'system' };
}

/** Store a choice: 'light' | 'dark' | 'system' (system = follow prefers-color-scheme). */
export function persistTheme(theme) {
  try { localStorage.setItem(THEME_STORAGE_KEY, theme); } catch { /* per-viewer convenience */ }
}

/** The Settings choice for a resolved source: only an explicit light/dark is a fixed mode. */
export function modeFor(resolved) {
  return resolved?.source === 'system' ? 'system' : resolved?.theme;
}

/** Resolve a mode ('light' | 'dark' | 'system') to the theme to render. */
export function themeForMode(mode, prefersLight) {
  if (mode === 'light' || mode === 'dark') return mode;
  return prefersLight ? 'light' : 'dark';
}

/** Resolve from the live environment; persists only an explicit ?theme= choice. */
export function initTheme(win = typeof window !== 'undefined' ? window : null) {
  if (!win) return { theme: 'dark', source: 'system', mode: 'system' };
  let stored = null;
  try { stored = win.localStorage.getItem(THEME_STORAGE_KEY); } catch { /* ignore */ }
  const resolved = resolveTheme({
    search: win.location?.search || '',
    cookie: win.document?.cookie || '',
    stored,
    prefersLight: !!win.matchMedia?.('(prefers-color-scheme: light)')?.matches,
  });
  if (resolved.source === 'query') persistTheme(resolved.theme);
  return { ...resolved, mode: modeFor(resolved) };
}
